import {
  BlueprintOperationTypes,
  type FlattenedWidgetProps,
  type WidgetDefaultProps,
} from "WidgetProvider/constants";
import { LayoutSystemTypes } from "layoutSystems/types";
import type { UpdatePropertyArgs } from "sagas/WidgetBlueprintSagas";
import { ResponsiveBehavior } from "layoutSystems/common/utils/constants";
import type { CanvasWidgetsReduxState } from "ee/reducers/entityReducers/canvasWidgetsReducer";
import { PluginPackageName } from "entities/Plugin";
import { type DynamicPath } from "utils/DynamicBindingUtils";
import type { ActionData } from "ee/reducers/entityReducers/actionsReducer";
import { createMessage } from "ce/constants/messages";
import { CHAT_SIZE } from "../../component/types";
import { AI_DATASOURCE } from "ee/constants/messages";

export const defaultsConfig = {
  chatHeightSize: CHAT_SIZE.FIT_PAGE,
  isVisible: true,
  widgetName: "AIChat",
  widgetType: "AI_CHAT",
  version: 1,
  responsiveBehavior: ResponsiveBehavior.Fill,
  initialAssistantMessage: `👋 Welcome! I’m Appsmith Agent — your AI companion to query data, perform actions, and answer questions across your tools.`,
  initialAssistantSuggestions: [],
  promptInputPlaceholder: "Ask me anything",
  assistantName: "Appsmith Agent",
  blueprint: {
    operations: [
      {
        type: BlueprintOperationTypes.ADD_ACTION,
        actionType: "CREATE_OR_UPDATE_DATASOURCE_WITH_ACTION",
        payload: {
          datasourceName: createMessage(AI_DATASOURCE),
          pluginPackageName: PluginPackageName.APPSMITH_AI,
          actionConfig: {
            name: "ChatQuery",
            // TODO: Need to get these values from Plugin Form configs.
            actionConfiguration: {
              formData: {
                usecase: { data: "AI_CHAT_ASSISTANT" },
                aiChatAssistant: {
                  input: {
                    instructions: "",
                    provider: "openai",
                    model: "gpt-4.1-mini",
                    requestType: "{{this.params.requestType || 'getThreads'}}",
                    message: "{{this.params.message}}",
                    widgetId: "{{this.params.widgetId}}",
                    threadId: "{{this.params.threadId}}",
                    citationId: "{{this.params.citationId}}",
                    messageId: "{{this.params.messageId}}",
                    limit: "{{this.params.limit}}",
                    username: "{{this.params.appsmithUsername}}",
                    kValue: 5,
                    minSimilarityThreshold: 0.0,
                    temperature: 0.7,
                    topP: 0.7,
                    searchAllDocuments: true,
                    reRankModel: "jina",
                    containsSelectedDocuments: true,
                  },
                },
              },
              timeoutInMillisecond: 60000,
            },
          },
        },
      },
      {
        type: BlueprintOperationTypes.MODIFY_PROPS,
        fn: (
          widget: FlattenedWidgetProps,
          widgets: CanvasWidgetsReduxState,
          _parent: FlattenedWidgetProps,
          layoutSystemType: LayoutSystemTypes,
          addActionResult: ActionData,
        ) => {
          if (layoutSystemType !== LayoutSystemTypes.ANVIL) return [];

          const dynamicBindingPathList: DynamicPath[] = [
            {
              key: "queryData",
            },
            {
              key: "queryRun",
            },
          ];

          const updates: UpdatePropertyArgs[] = [];
          const parentId = widget.parentId;

          if (parentId) {
            const parentWidget = widgets[parentId];

            // we want to proceed only if the parent is a zone widget and has no children
            if (
              parentWidget.children?.length === 0 &&
              parentWidget.type === "ZONE_WIDGET"
            ) {
              updates.push({
                widgetId: parentId,
                propertyName: "elevatedBackground",
                propertyValue: false,
              });
            }
          }

          updates.push(
            {
              widgetId: widget.widgetId,
              propertyName: "queryData",
              propertyValue: `{{${addActionResult.config.name}.data}}`,
            },
            {
              widgetId: widget.widgetId,
              propertyName: "queryRun",
              propertyValue: `{{${addActionResult.config.name}.actionId}}`,
            },
            {
              widgetId: widget.widgetId,
              propertyName: "dynamicBindingPathList",
              propertyValue: dynamicBindingPathList,
            },
          );

          return updates;
        },
      },
    ],
  },
} as unknown as WidgetDefaultProps;
